import React, { useState } from 'react';
import PropTypes from 'prop-types';
import css from './PreferencesSection.scss';
import { Card } from 'semantic-ui-react';
import classnames from 'classnames';
import { isEmptyString } from 'utils/text';
import Icon from 'SF/MaterialIcon';
import { pressedEnterOrSpace } from 'utils/accessibility';
import { useWindowSize } from 'react-use';

/**
 * @returns {node} the generic component for each section of preferences. This is used to
 * maintain consistent styling across each preference section in the profile.
 */
const PreferencesSection = ({ children, className, descriptionText, headerText, isStaff }) => {
    const MEDIUM_SCREEN_MAX_WIDTH = 812;
    
    // Monitor window width
    const { width } = useWindowSize();
    const allowCollapsibility = width <= MEDIUM_SCREEN_MAX_WIDTH && isStaff;

    const [ showContent, setShowContent ] = useState(!allowCollapsibility);
    
    const onClickCollapse = () => allowCollapsibility
        ? { onClick: () => setShowContent(!showContent) }
        : {};
    
    const caret = () =>
        allowCollapsibility ?
            <Icon icon={showContent ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                  className={css['caret']}
                  tabIndex="0"
                  aria-label={`${showContent ? 'Collapse' : 'Expand'} ${headerText}`}
                  onKeyDown={(e) => {
                      if (pressedEnterOrSpace(e)) {
                          e.preventDefault();
                          setShowContent(!showContent);
                      }
                  }} />
            : null;
    
    return (
        <Card className={classnames(css['profile-card'], {[css.collapsible]: allowCollapsibility})}>
            <Card.Content className={css['profile-card-title']} 
                          {...onClickCollapse()}>
                <Card.Header className={css['profile-card-header']}>
                    {headerText}
                    {caret()}
                </Card.Header>
                { descriptionText &&
                    <Card.Description className={css['profile-card-description']}>{descriptionText}</Card.Description>
                }
            </Card.Content>
            { (showContent || !allowCollapsibility) &&
                <Card.Content extra className={classnames(css['profile-card-options'], { [className]: !isEmptyString(className) })}>
                    {children}
                </Card.Content>
            }
        </Card>
    );
};

PreferencesSection.defaultProps = {
    isStaff: false
};

PreferencesSection.propTypes = {
    className: PropTypes.string,
    descriptionText: PropTypes.string,
    headerText: PropTypes.node.isRequired,
    isStaff: PropTypes.bool,
    children: PropTypes.node.isRequired
};

export default PreferencesSection;
